import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Search, Pencil, MoreVertical, Layers, Clock, FileText } from 'lucide-react';
import api from '../api/axios';
import JDModal from '../components/JDModal'; 

const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
`;

const JDRepository = () => {
  const [jds, setJds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingJD, setEditingJD] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => { 
    fetchJDs(); 

    // Listen for global header refresh
    const handleGlobalRefresh = () => fetchJDs();
    window.addEventListener('app-refresh', handleGlobalRefresh);
    return () => window.removeEventListener('app-refresh', handleGlobalRefresh);
  }, []);

  const fetchJDs = async () => {
    try {
      const response = await api.get('/jds/');
      setJds(response.data);
    } catch (error) { console.error("Error fetching JDs", error); } 
  };

  const filteredJDs = useMemo(() => {
    return jds.filter(jd => 
      jd.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      jd.skills.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [jds, searchQuery]);

  const latestUpdate = jds.length > 0 
    ? new Date(Math.max(...jds.map(jd => new Date(jd.created_at)))).toLocaleDateString()
    : "N/A";
  
  return (
    // UPDATED: Now supports bg-slate-50 for light mode and bg-[#05050A] for dark mode
    <div className="flex-1 min-h-screen bg-slate-50 dark:bg-[#05050A] text-slate-900 dark:text-slate-200 font-sans flex flex-col relative overflow-hidden transition-colors duration-500">
      <style>{customStyles}</style>

      {/* Ambient Glows */}
      <div className="absolute top-[-10%] left-[10%] w-[50%] h-[50%] rounded-full bg-indigo-200/40 dark:bg-indigo-600/10 blur-[120px] animate-blob z-0 pointer-events-none"></div>
      <div className="absolute bottom-[10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-violet-200/40 dark:bg-violet-600/10 blur-[120px] animate-blob z-0 pointer-events-none" style={{ animationDelay: '2s' }}></div>

      <div className="flex-1 overflow-y-auto px-6 lg:px-12 py-12 lg:py-20 w-full max-w-[1600px] mx-auto custom-scrollbar relative z-10">
        
        {/* --- MINIMALIST STATS CARDS --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 dark:bg-white/10 border border-slate-200 dark:border-white/10 rounded-[2rem] overflow-hidden mb-16">
          <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-8 flex flex-col justify-between hover:bg-slate-50 dark:hover:bg-[#111111]/80 transition-colors">
            <div className="flex items-center gap-3 mb-8">
              <div className="text-indigo-600 dark:text-indigo-400"><Layers size={18} strokeWidth={1.5} /></div>
              <span className="text-sm font-semibold tracking-wide text-slate-500 dark:text-slate-400">Total Job Descriptions</span>
            </div>
            <span className="text-5xl font-light tracking-tighter text-slate-900 dark:text-white">{jds.length}</span>
          </div>

          <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-8 flex flex-col justify-between hover:bg-slate-50 dark:hover:bg-[#111111]/80 transition-colors">
            <div className="flex items-center gap-3 mb-8">
              <div className="text-violet-600 dark:text-violet-400"><Clock size={18} strokeWidth={1.5} /></div>
              <span className="text-sm font-semibold tracking-wide text-slate-500 dark:text-slate-400">Last Updated</span>
            </div>
            <span className="text-4xl font-light tracking-tight text-slate-900 dark:text-white">{latestUpdate}</span>
          </div>
        </div>

        {/* --- SEARCH & ACTION BAR --- */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-12 border-b border-slate-200 dark:border-white/10 pb-6">
          <h2 className="text-2xl font-semibold tracking-wide text-slate-900 dark:text-white">Saved Descriptions</h2>
          
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
            <div className="relative w-full sm:w-72 group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 transition-colors" size={16} />
              <input 
                type="text" 
                placeholder="Search descriptions..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-2xl text-sm placeholder:text-[12px] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all shadow-sm dark:shadow-none"
              />
            </div>
            
            <button 
              onClick={() => { setEditingJD(null); setShowModal(true); }} 
              className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white px-8 py-3 rounded-2xl text-xs font-bold tracking-wide flex items-center justify-center gap-2 shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] transition-all active:scale-95 whitespace-nowrap"
            >
              <Plus size={16} strokeWidth={2}/> Create New
            </button>
          </div>
        </div>

        {/* --- JD GRID --- */}
        {filteredJDs.length === 0 ? (
          <div className="text-center py-32 bg-white dark:bg-white/5 backdrop-blur-sm rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none">
            <div className="w-16 h-16 bg-slate-50 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 border border-slate-100 dark:border-white/10">
              <FileText size={24} className="text-slate-400" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-medium tracking-wide text-slate-900 dark:text-white mb-2">No Descriptions Found</h3>
            <p className="text-slate-500 text-xs tracking-wide">Create a new JD or adjust your search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredJDs.map((jd, idx) => (
              <div 
                key={jd.id} 
                className="bg-white dark:bg-white/5 backdrop-blur-md p-7 rounded-[2rem] border border-slate-200 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/15 hover:bg-slate-50 dark:hover:bg-white/10 transition-all duration-300 group relative flex flex-col h-[260px] shadow-sm dark:shadow-none"
                style={{ animationDelay: `${idx * 50}ms`, animationFillMode: 'both' }}
              >
                {/* Floating Actions */}
                <div className="absolute top-5 right-5 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                  <button onClick={() => { setEditingJD(jd); setShowModal(true); }} className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-black/40 hover:bg-slate-200 dark:hover:bg-white/10 rounded-xl transition-all border border-transparent dark:hover:border-white/10">
                    <Pencil size={14} />
                  </button>
                  <button className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-black/40 hover:bg-slate-200 dark:hover:bg-white/10 rounded-xl transition-all border border-transparent dark:hover:border-white/10">
                    <MoreVertical size={14} />
                  </button>
                </div>
                
                <h3 className="font-medium dark:font-medium text-slate-900 dark:text-white text-xl mb-1.5 pr-16 line-clamp-2 leading-snug">{jd.title}</h3>
                <p className="text-[12px] text-slate-500 dark:text-slate-400 mb-4 font-semibold tracking-wide">Created: {new Date(jd.created_at).toLocaleDateString()}</p>
                
                {/* SKILLS SECTION */}
                <div className="relative group/skills w-full h-[24px] mb-2">
                  <div className="flex items-center gap-2 overflow-hidden w-full h-full">
                    {jd.skills.split(',').map((s, i) => (
                      <span key={i} className="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 text-[12px] rounded-md font-bold tracking-wide border border-indigo-200 dark:border-indigo-500/20 whitespace-nowrap">
                        {s.trim()}
                      </span>
                    ))}
                  </div>
                  
                  {/* Hover Reveal Popup */}
                  <div className="absolute top-[-10px] left-[-10px] w-[calc(100%+20px)] z-30 opacity-0 invisible group-hover/skills:opacity-100 group-hover/skills:visible transition-all duration-200">
                    <div className="flex flex-wrap gap-2 bg-white/95 dark:bg-[#111]/95 backdrop-blur-xl p-3 rounded-xl border border-slate-200 dark:border-white/10 shadow-xl">
                      {jd.skills.split(',').map((s, i) => (
                        <span key={i} className="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 text-[12px] rounded-md font-bold tracking-wide border border-indigo-200 dark:border-indigo-500/20 whitespace-nowrap">
                          {s.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="mt-auto flex flex-col justify-end">
                  <span className="text-slate-400 dark:text-slate-500 font-semibold text-[12px] tracking-wide block mb-1">About Role</span>
                  <span className="text-slate-600 dark:text-slate-300 line-clamp-2 text-sm font-light leading-relaxed">
                    {jd.full_text ? jd.full_text.replace(/[*#]/g, '').substring(0, 110) + '...' : "We are looking for a talented individual to join our team..."}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <JDModal onClose={() => setShowModal(false)} initialData={editingJD} onSuccess={() => fetchJDs()} />
      )}
    </div>
  );
};

export default JDRepository;